import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { CostDocumentListResponse } from "@/models/api/costDocuments/CostDocumentList";
import { CostDocumentDetails } from "@/models/api/costDocuments/CostDocumentDetails";
import {
  CostDocumentItemsPairs,
  CostDocumentItemsPairsResponse,
} from "@/models/api/costDocuments/CostDocumentItemsPairs";
import { CostDocumentEdit } from "@/models/api/costDocuments/CostDocumentEdit";
import { CostDocumentNumberValidation } from "@/models/api/costDocuments/CostDocumentNumberValidation";
import { FileGuid, FileTmp } from "@/models/api/Files";
import { CostDocumentToPair } from "@/models/api/costDocuments/CostDocumentToPair";
import { DocumentItemsToPair } from "@/models/api/costDocuments/DocumentItemsToPair";
import { DocumentItemPairDetails } from "@/models/api/costDocuments/DocumentItemPairDetails";
import { UpdateExtendPairData } from "@/models/api/costDocuments/UpdateExtendPairData";
import { ExtendCostDocumentItemPair } from "@/models/api/costDocuments/ExtendCostDocumentItemPair";
import {
  CostDocumentDataAfterPair,
  UpdatePairCostDocumentItem,
} from "@/models/api/costDocuments/CostDocumentDataAfterPair";
import { BookingItemForCostDocumentResponse } from "@/models/api/bookings/BookingForCostDocument";
import { CostDocumentSettledResult } from "@/models/api/costDocuments/CostDocumentSettledResult";
import { SelectItem } from "@/models/api/SelectItem";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { RelatedDocument } from "@/models/api/costDocuments/RelatedDocument";

//#region Routes
export const URL_GET_COST_DOCUMENTS_PAGED = "/CostDocument/CostDocuments/Paged";
export const URL_GET_COST_DOCUMENT_DETAILS = "/CostDocument/${CostDocumentId}/CostDocumentDetailsData";
export const URL_GET_COST_DOCUMENT_DATA = "/CostDocument/${CostDocumentId}/CostDocumentData";
export const URL_GET_COST_DOCUMENT_ITEMS_PAIR_FOR_COST_DOCUMENT =
  "/CostDocument/${CostDocumentId}/CostDocumentItemsPairForCostDocument";
export const URL_GET_COST_DOCUMENT_FILES = "/CostDocument/${CostDocumentId}/FileDataForCostDocument";
export const URL_POST_REMOVE_COST_DOCUMENT = "/CostDocument/${CostDocumentId}/Remove";
export const URL_GET_DATA_FOR_EDITED_COST_DOCUMENT = "/CostDocument/${CostDocumentId}/DataForEditedCostDocument";
export const URL_GET_DATA_FOR_CORRECTION_COST_DOCUMENT =
  "/CostDocument/${CostDocumentId}/DataForCorrectionCostDocument";
export const URL_GET_BOOKINGS_TO_IMPORT_FILES_PAGED = "/Booking/BookingsToImportFiles/Paged";
export const URL_POST_IMPORT_BOOKING_FILE = "/File/CreateTemporaryFileFromBookingFile";
export const URL_GET_VALIDATE_COST_DOCUMENT_NUMBER = "/CostDocument/ValidateDocumentNumber";

export const URL_POST_CREATE_COST_DOCUMENT = "/CostDocument/Create";
export const URL_POST_EDIT_COST_DOCUMENT = "/CostDocument/${CostDocumentId}/Edit";
export const URL_POST_CORRECT_COST_DOCUMENT = "/CostDocument/${CostDocumentId}/Correct";

export const URL_GET_DOCUMENT_ITEMS_TO_PAIR_PAGED = "/CostDocument/${CostDocumentItemId}/DocumentItemsToPair/Paged";
export const URL_GET_DOCUMENT_ITEM_DETAILS = "/CostDocument/${DocumentItemId}/DocumentItemDetails";
export const URL_GET_UPDATE_EXTEND_PAIR_DATA = "/CostDocument/${CostDocumentItemId}/UpdateExtendPairData";
export const URL_POST_SAVE_EXTEND_PAIR_COST_DOCUMENT_ITEM_WITH_DOCUMENT_ITEM =
  "/CostDocument/ExtendPairCostDocumentItemWithDocumentItem";
export const URL_POST_TOGGLE_COST_DOCUMENT_ITEM_IS_SETTLED =
  "/CostDocument/${CostDocumentItemId}/ToggleCostDocumentItemIsSettled";
export const URL_POST_TOGGLE_SALE_DOCUMENT_ITEM_IS_SETTLED =
  "/CostDocument/${DocumentItemId}/ToggleSaleDocumentItemIsSettled";
export const URL_POST_TOGGLE_DOCUMENT_WRITE_OFF = "/CostDocument/${DocumentItemId}/ToggleDocumentItemWriteOff";
export const URL_POST_DELETE_PAIR_COST_DOCUMENT_ITEM_WITH_DOCUMENT_ITEM =
  "/CostDocument/${CostDocumentItemId}/RemovePairCostDocumentItemWithDocumentItem/${DocumentItemId}";
export const URL_POST_SAVE_PAIR_COST_DOCUMENT_ITEM_WITH_DOCUMENT_ITEM =
  "/CostDocument/${CostDocumentItemId}/SavePairCostDocumentItemWithDocumentItem";
export const URL_POST_AUTOMATIC_SETTLE_ITEMS =
  "/CostDocument/${CostDocumentItemId}/AutomaticSettleItems/${DocumentItemId}";
export const URL_POST_AUTOMATIC_SETTLE_AND_WRITE_OFF_ITEMS =
  "/CostDocument/${CostDocumentItemId}/AutomaticSettleAndWriteOffItems/${DocumentItemId}";
export const URL_POST_SAVE_COST_DOCUMENT_ITEM_COMMENT =
  "/CostDocument/${CostDocumentItemId}/SaveCostDocumentItemComment";
export const URL_GET_DESCRIPTION_FROM_BOOKING_ATTRIBUTES =
  "/CostDocument/GetDescriptionFromBookingAttributes/${BookingId}";
export const URL_GET_COST_DOCUMENT_TYPES = "/CostDocument/CostDocumentTypes";
export const URL_GET_RELATED_SALE_DOCUMENTS = "/CostDocument/${CostDocumentId}/RelatedSaleDocuments";
export const URL_POST_PAIR_MULTIPLE_DOCUMENT_ITEMS_WITH_COST_DOCUMENT_ITEM =
  "/CostDocument/${CostDocumentItemId}/PairMultiple";

export const URL_POST_APPROVE_COST_DOCUMENT = "/CostDocument/${CostDocumentId}/Approve";

//#endregion

const _getCostDocumentsTable = new CancellableHelper<CostDocumentListResponse>(
  URL_GET_COST_DOCUMENTS_PAGED,
  CostDocumentListResponse
);
_getCostDocumentsTable.jsonSerializer = true;
export const getCostDocumentsTable = async (
  genericTableRequest: GenericTableRequest
): Promise<CostDocumentListResponse> => {
  return await _getCostDocumentsTable.get(genericTableRequest);
};

const _getCostDocumentDetails = new CancellableHelper<CostDocumentDetails>(
  URL_GET_COST_DOCUMENT_DETAILS,
  CostDocumentDetails
);
export const getCostDocumentDetails = async (costDocumentId: string): Promise<CostDocumentDetails> => {
  return await _getCostDocumentDetails.get({ CostDocumentId: costDocumentId });
};

const _getCostDocumentData = new CancellableHelper<CostDocumentToPair>(URL_GET_COST_DOCUMENT_DATA, CostDocumentToPair);
export const getCostDocumentData = async (costDocumentId: string): Promise<CostDocumentToPair> => {
  return await _getCostDocumentData.get({ CostDocumentId: costDocumentId });
};

const _getCostDocumentTypes = new CancellableHelper<Array<SelectItem>>(URL_GET_COST_DOCUMENT_TYPES, SelectItem);
export const getCostDocumentTypes = async (): Promise<Array<SelectItem>> => {
  return await _getCostDocumentTypes.get();
};

const _getCostDocumentItemsPairForCostDocument = new CancellableHelper<CostDocumentItemsPairsResponse>(
  URL_GET_COST_DOCUMENT_ITEMS_PAIR_FOR_COST_DOCUMENT,
  CostDocumentItemsPairs
);
export const getCostDocumentItemsPairForCostDocument = async (
  costDocumentId: string
): Promise<CostDocumentItemsPairsResponse> => {
  return await _getCostDocumentItemsPairForCostDocument.get({
    CostDocumentId: costDocumentId,
  });
};

const _getCostDocumentFiles = new CancellableHelper<Array<FileGuid>>(URL_GET_COST_DOCUMENT_FILES, FileGuid);
export const getCostDocumentFiles = async (costDocumentId: string): Promise<Array<FileGuid>> => {
  return await _getCostDocumentFiles.get({ CostDocumentId: costDocumentId });
};

const _removeCostDocument = new CancellableHelper<DoubleConfirm>(URL_POST_REMOVE_COST_DOCUMENT, null, true);
export const removeCostDocument = async (costDocumentId: string, confirmed: boolean): Promise<DoubleConfirm> => {
  return await _removeCostDocument.post({
    CostDocumentId: costDocumentId,
    Confirmed: confirmed,
  });
};

const _getDataForEditedCostDocument = new CancellableHelper<CostDocumentEdit>(
  URL_GET_DATA_FOR_EDITED_COST_DOCUMENT,
  CostDocumentEdit
);
export const getDataForEditedCostDocument = async (
  costDocumentId: string,
  sessionId: string
): Promise<CostDocumentEdit> => {
  return await _getDataForEditedCostDocument.get({
    CostDocumentId: costDocumentId,
    sessionId: sessionId,
  });
};

const _getDataForCorrectionCostDocument = new CancellableHelper<CostDocumentEdit>(
  URL_GET_DATA_FOR_CORRECTION_COST_DOCUMENT,
  CostDocumentEdit
);
export const getDataForCorrectionCostDocument = async (
  costDocumentId: string,
  sessionId: string
): Promise<CostDocumentEdit> => {
  return await _getDataForCorrectionCostDocument.get({
    CostDocumentId: costDocumentId,
    sessionId: sessionId,
  });
};

const _getBookingsToImportFile = new CancellableHelper<BookingItemForCostDocumentResponse>(
  URL_GET_BOOKINGS_TO_IMPORT_FILES_PAGED,
  BookingItemForCostDocumentResponse
);
_getBookingsToImportFile.jsonSerializer = true;
export const getBookingsToImportFile = async (
  param: GenericTableRequest
): Promise<BookingItemForCostDocumentResponse> => {
  return await _getBookingsToImportFile.get(param);
};

const _getDocumentItemsToPair = new CancellableHelper<DocumentItemsToPair>(
  URL_GET_DOCUMENT_ITEMS_TO_PAIR_PAGED,
  DocumentItemsToPair
);
_getDocumentItemsToPair.jsonSerializer = true;
export const getDocumentItemsToPair = async (
  param: GenericTableRequest,
  costDocumentItemId: null | number = null
): Promise<DocumentItemsToPair> => {
  return await _getDocumentItemsToPair.get({
    ...param,
    CostDocumentItemId: costDocumentItemId,
  });
};

const _getValidateCostDocumentNumber = new CancellableHelper<null>(URL_GET_VALIDATE_COST_DOCUMENT_NUMBER, null, true);
export const getValidateCostDocumentNumber = async (param: CostDocumentNumberValidation): Promise<null> => {
  return await _getValidateCostDocumentNumber.get(param);
};

const _createCostDocument = new CancellableHelper<null>(URL_POST_CREATE_COST_DOCUMENT, null, true);
export const createCostDocument = async (param: CostDocumentEdit): Promise<null> => {
  return await _createCostDocument.post(null, param);
};

const _editCostDocument = new CancellableHelper<DoubleConfirm>(URL_POST_EDIT_COST_DOCUMENT, null, true);
export const editCostDocument = async (param: CostDocumentEdit, confirmed: boolean): Promise<DoubleConfirm> => {
  return await _editCostDocument.post(
    {
      CostDocumentId: param.currentCostDocumentId,
      Confirmed: confirmed,
    },
    param
  );
};

const _correctCostDocument = new CancellableHelper<string>(URL_POST_CORRECT_COST_DOCUMENT, "string");
export const correctCostDocument = async (param: CostDocumentEdit): Promise<string> => {
  return await _correctCostDocument.post(
    {
      CostDocumentId: param.currentCostDocumentId,
    },
    param
  );
};

const _importFileFromBooking = new CancellableHelper<Array<FileTmp>>(URL_POST_IMPORT_BOOKING_FILE, FileTmp);
export const importFileFromBooking = async (
  bookingId: number,
  sessionId: string,
  withVouchers: boolean,
  loadAll: boolean
): Promise<Array<FileTmp>> => {
  return await _importFileFromBooking.post(
    {},
    {
      BookingId: bookingId,
      SessionId: sessionId,
      WithVouchers: withVouchers,
      LoadAll: loadAll,
    }
  );
};

const _getDocumentItemDetails = new CancellableHelper<DocumentItemPairDetails>(
  URL_GET_DOCUMENT_ITEM_DETAILS,
  DocumentItemPairDetails
);
export const getDocumentItemDetails = async (documentItemId: string): Promise<DocumentItemPairDetails> => {
  return await _getDocumentItemDetails.get({ DocumentItemId: documentItemId });
};

const _postToggleCostDocumentItemIsSettled = new CancellableHelper<null>(
  URL_POST_TOGGLE_COST_DOCUMENT_ITEM_IS_SETTLED,
  null,
  true
);
export const postToggleCostDocumentItemIsSettled = async (costDocumentItemId: number): Promise<null> => {
  return await _postToggleCostDocumentItemIsSettled.post({
    CostDocumentItemId: costDocumentItemId,
  });
};

const _postToggleSaleDocumentItemIsSettled = new CancellableHelper<CostDocumentSettledResult>(
  URL_POST_TOGGLE_SALE_DOCUMENT_ITEM_IS_SETTLED,
  CostDocumentSettledResult,
  true
);
export const postToggleSaleDocumentItemIsSettled = async (
  documentItemId: string,
  userSeeQuestion: boolean,
  costDocumentsToSetIds: number[]
): Promise<CostDocumentSettledResult> => {
  return await _postToggleSaleDocumentItemIsSettled.post(
    { DocumentItemId: documentItemId },
    {
      UserSeeQuestion: userSeeQuestion,
      CostDocumentsToSetIds: costDocumentsToSetIds,
    }
  );
};

const _postToggleSaleDocumentWriteOff = new CancellableHelper<null>(URL_POST_TOGGLE_DOCUMENT_WRITE_OFF, null, true);
export const postToggleSaleDocumentWriteOff = async (documentItemId: string): Promise<null> => {
  return await _postToggleSaleDocumentWriteOff.post({
    DocumentItemId: documentItemId,
  });
};

const _getUpdateExtendPairData = new CancellableHelper<UpdateExtendPairData>(
  URL_GET_UPDATE_EXTEND_PAIR_DATA,
  UpdateExtendPairData
);
export const getUpdateExtendPairData = async (
  costDocumentItemId: number,
  documentItemId: string,
  pairAmount: number
): Promise<UpdateExtendPairData> => {
  return await _getUpdateExtendPairData.get({
    CostDocumentItemId: costDocumentItemId,
    documentItemId: documentItemId,
    pairAmount: pairAmount,
  });
};

const _saveExtendPairCostDocumentItemWithDocumentItem = new CancellableHelper<CostDocumentDataAfterPair>(
  URL_POST_SAVE_EXTEND_PAIR_COST_DOCUMENT_ITEM_WITH_DOCUMENT_ITEM,
  CostDocumentDataAfterPair,
  true
);
export const saveExtendPairCostDocumentItemWithDocumentItem = async (
  extendCostDocumentItemPair: ExtendCostDocumentItemPair
): Promise<CostDocumentDataAfterPair> => {
  return await _saveExtendPairCostDocumentItemWithDocumentItem.post({}, extendCostDocumentItemPair);
};

const _removePairCostDocumentItemWithDocumentItem = new CancellableHelper<null>(
  URL_POST_DELETE_PAIR_COST_DOCUMENT_ITEM_WITH_DOCUMENT_ITEM,
  null,
  true
);
export const removePairCostDocumentItemWithDocumentItem = async (
  costDocumentItemId: number,
  documentItemId: string
): Promise<null> => {
  return await _removePairCostDocumentItemWithDocumentItem.post({
    CostDocumentItemId: costDocumentItemId,
    DocumentItemId: documentItemId,
  });
};

const _updatePairCostDocumentItemWithDocumentItem = new CancellableHelper<CostDocumentDataAfterPair>(
  URL_POST_SAVE_PAIR_COST_DOCUMENT_ITEM_WITH_DOCUMENT_ITEM,
  CostDocumentDataAfterPair,
  true
);
export const savePairCostDocumentItemWithDocumentItem = async (
  costDocumentItemId: number,
  documentItemId: string,
  pairAmount: number
): Promise<CostDocumentDataAfterPair> => {
  return await _updatePairCostDocumentItemWithDocumentItem.post(
    {
      CostDocumentItemId: costDocumentItemId,
    },
    {
      DocumentItemId: documentItemId,
      PairAmount: pairAmount,
    }
  );
};

const _pairMultipleDocumentItemsWithCostDocumentItem = new CancellableHelper<CostDocumentDataAfterPair>(
  URL_POST_PAIR_MULTIPLE_DOCUMENT_ITEMS_WITH_COST_DOCUMENT_ITEM,
  CostDocumentDataAfterPair,
  true
);
export const pairMultipleDocumentItemsWithCostDocumentItem = async (
  costDocumentItemId: number,
  pairs: Array<UpdatePairCostDocumentItem>
): Promise<CostDocumentDataAfterPair> => {
  return await _pairMultipleDocumentItemsWithCostDocumentItem.post({ CostDocumentItemId: costDocumentItemId }, pairs);
};

const _automaticSettleItems = new CancellableHelper<null>(URL_POST_AUTOMATIC_SETTLE_ITEMS, null, true);
export const automaticSettleItems = async (costDocumentItemId: number, documentItemId: string): Promise<null> => {
  return await _automaticSettleItems.post({
    CostDocumentItemId: costDocumentItemId,
    DocumentItemId: documentItemId,
  });
};

const _automaticSettleAndWriteOffItems = new CancellableHelper<null>(
  URL_POST_AUTOMATIC_SETTLE_AND_WRITE_OFF_ITEMS,
  null,
  true
);
export const automaticSettleAndWriteOffItems = async (
  costDocumentItemId: number,
  documentItemId: string
): Promise<null> => {
  return await _automaticSettleAndWriteOffItems.post({
    CostDocumentItemId: costDocumentItemId,
    DocumentItemId: documentItemId,
  });
};

const _saveCostDocumentItemComment = new CancellableHelper<null>(URL_POST_SAVE_COST_DOCUMENT_ITEM_COMMENT, null, true);
export const saveCostDocumentItemComment = async (
  costDocumentItemId: number,
  comment: string | null
): Promise<null> => {
  return await _saveCostDocumentItemComment.post(
    {
      CostDocumentItemId: costDocumentItemId,
    },
    {
      Comment: comment,
    }
  );
};

const _getDescriptionFromBookingAttributes = new CancellableHelper<string>(
  URL_GET_DESCRIPTION_FROM_BOOKING_ATTRIBUTES,
  "string",
  true
);
export const getDescriptionFromBookingAttributes = async (bookingId: number): Promise<string> => {
  return await _getDescriptionFromBookingAttributes.get({
    BookingId: bookingId,
  });
};

const _getRelatedSaleDocuments = new CancellableHelper<Array<RelatedDocument>>(
  URL_GET_RELATED_SALE_DOCUMENTS,
  RelatedDocument
);

export const getRelatedSaleDocuments = async (costDocumentId: string) => {
  return await _getRelatedSaleDocuments.get({
    CostDocumentId: costDocumentId,
  });
};

const _approveCostDocument = new CancellableHelper<string>(URL_POST_APPROVE_COST_DOCUMENT);
export const approveCostDocument = async (costDocumentId: string): Promise<string> => {
  return await _approveCostDocument.post({ CostDocumentId: costDocumentId });
};
